import { useLazyQuery } from '@apollo/client/react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Popover, Spin, Typography } from 'antd';
import moment from 'moment';
import React from 'react';
import { USER_CHANGE_HISTORY } from '../modules/organizations/graphql/Queries';

// Newest page only — an audit trail grows without bound, and every open refetches (network-only).
const HISTORY_PAGE_SIZE = 20;

// US #634 (US2): admin-only ⓘ beside a user's name in the organisation user list showing the read-only,
// newest-first history of name/email/phone changes. Loaded lazily when the ⓘ is opened.
const FIELD_LABELS = {
  NAME: 'Name',
  EMAIL: 'Email',
  PHONE: 'Phone',
  DESIGNATION: 'Designation',
  ORG_ROLE: 'Organisation role',
};

const ChangeHistoryEntry = ({ row, userId }) => {
  const isSelfEdit =
    row?.editor?.id && userId && String(row?.editor?.id) === String(userId);
  return (
    <div className="change-history-entry">
      <div className="change-history-field">
        {FIELD_LABELS[row?.field] || row?.field}
      </div>
      <div className="change-history-values">
        <span className="change-history-old">{row?.oldValue || '-'}</span>
        <span className="change-history-arrow">→</span>
        <span className="change-history-new">{row?.newValue || '-'}</span>
      </div>
      <div className="change-history-meta">
        {row?.editor?.name || '-'}
        {row?.createdAt
          ? ` · ${moment(row?.createdAt).format('DD/MM/YYYY HH:mm')}`
          : ''}
        {isSelfEdit ? ' · edited their own profile' : ''}
      </div>
    </div>
  );
};

const ChangeHistoryContent = ({
  loading = false,
  error = false,
  rows = [],
  total = 0,
  userId,
  onRetry,
}) => {
  if (loading) return <Spin size="small" />;
  if (error) {
    return (
      <div className="change-history-error">
        <Typography.Text type="danger">
          Unable to load change history. Please try again.
        </Typography.Text>
        <Button size="small" type="link" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  if (!rows?.length) {
    return (
      <div className="change-history-panel">
        <Typography.Text type="secondary">No changes recorded.</Typography.Text>
      </div>
    );
  }
  return (
    <div className="change-history-panel">
      {rows?.map((row) => (
        <ChangeHistoryEntry key={row?.id} row={row} userId={userId} />
      ))}
      <div className="change-history-foot">
        {total > rows?.length
          ? `Showing ${rows?.length} of ${total} changes. `
          : ''}
        Newest first. Read-only — no entry can be edited or deleted, by anyone.
      </div>
    </div>
  );
};

const ChangeHistoryInfo = ({ userId, userName, tenantId }) => {
  const [fetchHistory, { data, loading, error }] = useLazyQuery(
    USER_CHANGE_HISTORY,
    {
      fetchPolicy: 'network-only',
    },
  );

  const rows = data?.userChangeHistory?.data || [];
  const total = data?.userChangeHistory?.total || 0;

  // Apollo Client v4's lazy execute does not apply hook-level `variables` on a bare call, so pass
  // them explicitly each time (also avoids forwarding the click event as options on Retry).
  const loadHistory = () =>
    fetchHistory({
      variables: { filter: { userId, tenantId, limit: HISTORY_PAGE_SIZE } },
    });

  const handleOpenChange = (open) => {
    if (open) loadHistory();
  };

  return (
    <Popover
      trigger="click"
      title={`Change history${userName ? ` — ${userName}` : ''}`}
      placement="bottomLeft"
      // antd v6 renamed overlayClassName; classNames.root is the supported hook
      classNames={{ root: 'change-history-popover' }}
      onOpenChange={handleOpenChange}
      // antd v6: destroyTooltipOnHide was dropped in favour of destroyOnHidden. Under the old name
      // the prop is simply unrecognised and the previous rows stay mounted, flashing on re-open.
      destroyOnHidden
      content={
        <ChangeHistoryContent
          loading={loading}
          error={!!error}
          rows={rows}
          total={total}
          userId={userId}
          onRetry={loadHistory}
        />
      }
    >
      {/* a Button, not a bare icon: the popover is a real control, so it needs to be focusable and
          operable with Enter/Space, and to expose an accessible name. */}
      <Button
        type="text"
        size="small"
        className="change-history-info-icon"
        aria-label={`View change history${userName ? ` for ${userName}` : ''}`}
        icon={<InfoCircleOutlined />}
      />
    </Popover>
  );
};

export default ChangeHistoryInfo;
